import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import AddEditCommonCodeModal from "./AddEditCommonCodeModal";

interface CommonCodeListState {
    commonCodes: any[];
    groupCodes: Array<{ code: string; name: string }>;
    pageSize: number;
    criteria: string;
    groupCodeFilter: string | null;
    isActiveFilter: boolean | null;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
    modalOpen: boolean;
    selectedCode: any | null;
    deleteDialogOpen: boolean;
    codeToDelete: any | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_commoncode_page_size";

interface CommonCodeListScreenProps {
    fixedGroupCode?: string;
    darkMode?: boolean;
}

class CommonCodeListScreenClass extends Component<CommonCodeListScreenProps, CommonCodeListState> {
    constructor(props: CommonCodeListScreenProps) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            commonCodes: [],
            groupCodes: [],
            pageSize: initialPageSize,
            criteria: "",
            groupCodeFilter: props.fixedGroupCode || null,
            isActiveFilter: null,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
            modalOpen: false,
            selectedCode: null,
            deleteDialogOpen: false,
            codeToDelete: null,
        };
    }

    async componentDidMount() {
        await this.loadGroupCodes();
        this.loadCommonCodes();
    }

    loadGroupCodes = async () => {
        try {
            const res = await request("CommonCode.getGroups");
            if (res && res.data) {
                const uniqueGroups = res.data
                    .map((group: any) => ({
                        code: group.groupCode,
                        name: group.groupName
                    }))
                    .sort((a: any, b: any) => a.code.localeCompare(b.code));

                this.setState({ groupCodes: uniqueGroups });
            }
        } catch (error: any) {
            console.error("그룹코드 목록 조회 실패:", error);
        }
    };

    loadCommonCodes = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                Name: this.state.criteria || undefined,
                GroupCode: this.state.groupCodeFilter || undefined,
                IsActive: this.state.isActiveFilter,
            };

            const res = await request("CommonCode.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const commonCodes = (pagedList.items || []).map((code: any) => ({
                    seqId: code.seqId,
                    groupCode: code.groupCode || "-",
                    code: code.code || "-",
                    name: code.name || "-",
                    description: code.description || "-",
                    sortOrder: code.sortOrder ?? "-",
                    isActive: code.isActive,
                }));

                this.setState({
                    commonCodes,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("공통코드 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadCommonCodes();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadCommonCodes();
        });
    };

    handleSearch = (searchValue: string) => {
        this.setState({ criteria: searchValue, pageNumber: 1 }, () => {
            this.loadCommonCodes();
        });
    };

    handleRowClick = (row: any) => {
        this.setState({ modalOpen: true, selectedCode: row });
    };

    openAddModal = () => {
        this.setState({ modalOpen: true, selectedCode: null });
    };

    closeModal = () => {
        this.setState({ modalOpen: false, selectedCode: null });
    };

    handleModalSuccess = () => {
        this.closeModal();
        this.loadCommonCodes();
    };

    handleDelete = (code: any, event: React.MouseEvent) => {
        event.stopPropagation();
        this.setState({ deleteDialogOpen: true, codeToDelete: code });
    };

    confirmDelete = async () => {
        const { codeToDelete } = this.state;
        if (!codeToDelete) return;

        try {
            await request("CommonCode.delete", {
                pathParams: { seqId: codeToDelete.seqId.toString() },
            });
            this.setState({ deleteDialogOpen: false, codeToDelete: null });
            this.loadCommonCodes();
        } catch (error: any) {
            console.error("공통코드 삭제 실패:", error);
            alert(error?.response?.data?.message || "공통코드 삭제에 실패했습니다.");
        }
    };

    cancelDelete = () => {
        this.setState({ deleteDialogOpen: false, codeToDelete: null });
    };

    render() {
        const { commonCodes, pageSize, criteria, loading, totalCount, pageNumber, modalOpen, selectedCode, deleteDialogOpen, codeToDelete } = this.state;
        const { fixedGroupCode } = this.props;

        const columns = [

            { Header: "그룹코드", accessor: "groupCode", align: "left", width: "15%" },
            { Header: "코드", accessor: "code", align: "left", width: "15%" },
            { Header: "이름", accessor: "name", align: "left", width: "20%" },
            { Header: "설명", accessor: "description", align: "left", width: "25%" },
            { Header: "정렬순서", accessor: "sortOrder", align: "center", width: "10%" },
            {
                Header: "활성화",
                accessor: "isActive",
                align: "center",
                width: "10%",
                Cell: ({ value }: any) => (
                    <Chip
                        label={value ? "활성" : "비활성"}
                        color={value ? "success" : "error"}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            minWidth: 60,
                            ...(value && {
                                backgroundColor: "#4CAF50",
                                color: "#ffffff",
                                "& .MuiChip-label": {
                                    color: "#ffffff",
                                },
                            }),
                        }}
                    />
                )
            },
            {
                Header: "작업",
                accessor: "actions",
                align: "center",
                width: "10%",
                Cell: ({ row }: any) => (
                    <MDBox display="flex" justifyContent="center">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                this.handleRowClick(row.original);
                            }}
                            sx={{
                                color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": {
                                    backgroundColor: this.props.darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                    color: "#49a3f1",
                                },
                            }}
                        >
                            <Icon>edit</Icon>
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => this.handleDelete(row.original, e)}
                            sx={{
                                color: this.props.darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": {
                                    backgroundColor: this.props.darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                    color: "#f44336",
                                },
                            }}
                        >
                            <Icon>delete</Icon>
                        </IconButton>
                    </MDBox>
                )
            },
        ];

        // If fixedGroupCode is provided, hide the groupCode column or keep it?
        // Let's keep it for verification but maybe hide if desired. For now, keep it.

        const rows = commonCodes;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>code</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        {fixedGroupCode ? "카테고리 관리" : "공통코드 관리"}
                                    </MDTypography>
                                </MDBox>
                                <MDButton
                                    variant="gradient"
                                    color="info"
                                    onClick={this.openAddModal}
                                >
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                        add
                                    </Icon>
                                    {fixedGroupCode ? "카테고리 추가" : "공통코드 추가"}
                                </MDButton>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                {fixedGroupCode ? "카테고리 목록" : "공통코드 목록"} (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>

                        {/* Filters */}
                        {!fixedGroupCode && (
                            <MDBox px={3} pb={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete<{ code: string; name: string }, false, false, false>
                                            options={[
                                                { code: "전체", name: "전체" },
                                                ...this.state.groupCodes
                                            ]}
                                            value={
                                                this.state.groupCodeFilter
                                                    ? this.state.groupCodes.find(g => g.code === this.state.groupCodeFilter) || { code: "전체", name: "전체" }
                                                    : { code: "전체", name: "전체" }
                                            }
                                            getOptionLabel={(option) =>
                                                option.code === "전체" ? "전체" : `${option.code} - ${option.name}`
                                            }
                                            isOptionEqualToValue={(option, value) => option.code === value.code}
                                            onChange={(_, newValue) => {
                                                this.setState(
                                                    { groupCodeFilter: newValue?.code === "전체" ? null : newValue?.code || null, pageNumber: 1 },
                                                    () => this.loadCommonCodes()
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <MDInput {...params} label="그룹코드" />
                                            )}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete
                                            options={[
                                                { label: "전체", value: null },
                                                { label: "활성", value: true },
                                                { label: "비활성", value: false },
                                            ]}
                                            getOptionLabel={(option: any) => option.label}
                                            value={
                                                this.state.isActiveFilter === null
                                                    ? { label: "전체", value: null }
                                                    : this.state.isActiveFilter
                                                        ? { label: "활성", value: true }
                                                        : { label: "비활성", value: false }
                                            }
                                            onChange={(_, newValue: any) => {
                                                this.setState(
                                                    { isActiveFilter: newValue?.value ?? null, pageNumber: 1 },
                                                    () => this.loadCommonCodes()
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <MDInput {...params} label="활성화 상태" />
                                            )}
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </MDBox>
                        )}

                        <BasicTable
                            canSearch={!fixedGroupCode}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                            onRowClick={this.handleRowClick}
                            onSearch={this.handleSearch}
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />

                {/* Add/Edit Modal */}
                {modalOpen && (
                    <AddEditCommonCodeModal
                        open={modalOpen}
                        onClose={this.closeModal}
                        onSuccess={this.handleModalSuccess}
                        commonCode={selectedCode}
                        fixedGroupCode={fixedGroupCode}
                    />
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={this.cancelDelete}>
                    <DialogTitle>공통코드 삭제</DialogTitle>
                    <DialogContent>
                        <MDTypography variant="body2">
                            정말로 이 공통코드를 삭제하시겠습니까?
                            <br />
                            <strong>
                                {codeToDelete?.groupCode} - {codeToDelete?.code}
                            </strong>
                        </MDTypography>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.cancelDelete} variant="outlined" color="dark">
                            취소
                        </MDButton>
                        <MDButton onClick={this.confirmDelete} variant="gradient" color="error">
                            삭제
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

export default function CommonCodeListScreen({ fixedGroupCode }: { fixedGroupCode?: string }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    return <CommonCodeListScreenClass darkMode={darkMode} fixedGroupCode={fixedGroupCode} />;
}
